/**
 * Lightweight URL detection for link input previews (YouTube, Instagram, TikTok, etc.).
 */

export type LinkPreviewKind =
  | "youtube"
  | "instagram"
  | "tiktok"
  | "twitter"
  | "threads"
  | "linkedin"
  | null;

export interface LinkPreviewInfo {
  kind: LinkPreviewKind;
  label: string;
  subtitle: string;
  thumbnailUrl?: string;
}

const YOUTUBE_RE =
  /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
const INSTAGRAM_RE = /(?:instagram\.com|instagr\.am)\/(?:reels?|p|tv)\/([A-Za-z0-9_-]+)/i;
const TIKTOK_RE =
  /(?:tiktok\.com\/@[^/]+\/video\/\d+|vm\.tiktok\.com\/[A-Za-z0-9]+|tiktok\.com\/t\/[A-Za-z0-9]+)/i;
const TWITTER_RE = /(?:twitter\.com|x\.com)\/\w+\/status\/\d+/i;
const THREADS_RE = /threads\.net\/@[^/]+\/post\/[A-Za-z0-9_-]+/i;
const LINKEDIN_RE = /linkedin\.com\/(?:posts|feed\/update|pulse)\//i;

export function getLinkPreview(url: string): LinkPreviewInfo | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  const yt = trimmed.match(YOUTUBE_RE);
  if (yt) {
    const id = yt[1];
    return {
      kind: "youtube",
      label: "YouTube video detected",
      subtitle: `youtube.com/watch?v=${id}`,
      thumbnailUrl: `https://img.youtube.com/vi/${id}/mqdefault.jpg`,
    };
  }

  const ig = trimmed.match(INSTAGRAM_RE);
  if (ig) {
    const isReel = /\/reels?\//i.test(trimmed);
    const isTv = /\/tv\//i.test(trimmed);
    const label = isReel ? "Instagram Reel detected" : isTv ? "Instagram video detected" : "Instagram post detected";
    return {
      kind: "instagram",
      label,
      subtitle: trimmed.replace(/^https?:\/\//, "").split("?")[0],
    };
  }

  if (TIKTOK_RE.test(trimmed)) {
    return {
      kind: "tiktok",
      label: "TikTok video detected",
      subtitle: trimmed.replace(/^https?:\/\//, "").split("?")[0],
    };
  }

  if (TWITTER_RE.test(trimmed)) {
    return {
      kind: "twitter",
      label: "X post detected",
      subtitle: trimmed.replace(/^https?:\/\//, "").split("?")[0],
    };
  }

  if (THREADS_RE.test(trimmed)) {
    return {
      kind: "threads",
      label: "Threads post detected",
      subtitle: trimmed.replace(/^https?:\/\//, "").split("?")[0],
    };
  }

  if (LINKEDIN_RE.test(trimmed)) {
    return {
      kind: "linkedin",
      label: "LinkedIn post detected",
      subtitle: trimmed.replace(/^https?:\/\//, "").split("?")[0],
    };
  }

  return null;
}

export function extractYouTubeId(url: string): string | null {
  const m = url.match(YOUTUBE_RE);
  return m ? m[1] : null;
}
